'use client'

import AlertDialog from '@/components/design-system/alert-dialog'
import Tooltip from '@/components/design-system/tooltip'
import ChestIcon from '@/components/icons/chest'
import ChevronDownIcon from '@/components/icons/chevron-down'
import CoinIcon from '@/components/icons/coin'
import { DollarCellIcon } from '@/components/icons/dollar-cell'
import LogoutIcon from '@/components/icons/logout'
import QuestionBallonIcon from '@/components/icons/question-ballon'
import SettingsIcon from '@/components/icons/settings'
import ShootingStarIcon from '@/components/icons/shooting-star'
import UserIcon from '@/components/icons/user'
import VerifyIcon from '@/components/icons/verify'
import WithdrawIcon from '@/components/icons/withdraw'
import { useTransactionModal } from '@/components/transaction/useTransactionModal'
import { Avatar } from '@/components/ui/avatar'
import { IconButton } from '@/components/ui/icon-button'
import { Image } from '@/components/ui/image'
import {
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
} from '@/components/ui/menu'
import { Text } from '@/components/ui/text'
import useAuth from '@/hooks/use-auth'
import { Link, useRouter } from '@/navigation'
import { Flex } from '@/styled-system/jsx'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { useSearchParams } from 'next/navigation'

export default function HeaderUserMenu() {
  const t = useTranslations('header.user-dropdown')
  const [showAlert, setShowAlert] = useState(false)
  //const { onOpen: openTransactionModal } = useTransactionModal()
  const { logout, user } = useAuth()

  const router = useRouter()
  const isAdmin = false

  const searchParams = useSearchParams()

  const isOpen = searchParams.get('modal') === 'logout'

  return (
    <>
      <Menu positioning={{ placement: 'bottom-end' }} variant='secondary'>
        <MenuTrigger asChild>
          <Flex align='center' h='11' w='fit' maxW='206px'>
            <Avatar
              name={user?.name ?? 'User'}
              variant='unstyled'
              size='lg'
              border='1px solid'
              borderColor='#251929'
              borderRadius='md'
              borderRightRadius={0}
              display={{
                base: 'none!',
                sm: 'grid!',
                md: 'none!',
                lg: 'grid!',
              }}
              icon={
                <Image
                  src='/assets/avatar_default.jpg'
                  alt='pig'
                  h='11'
                  w='11'
                />
              }
            />

            <Flex
              display={{ base: 'none', sm: 'flex', md: 'none', lg: 'flex' }}
              align='center'
              borderY='1px solid'
              borderColor='#251929'
              flex='1'
              gap='2'
              px='2'
              h='full'
              transition='all 300ms ease'
              _hover={{
                bgColor: '#1A131C',
              }}
            >
              <Tooltip title={user?.name ?? 'User'}>
                <Text
                  as='span'
                  color='text.primary'
                  cursor='pointer'
                  fontSize='sm'
                  fontWeight='semibold'
                  flex='1'
                  textAlign='center'
                  lineHeight='none'
                  maxW='94px'
                  truncate
                >
                  {user?.name ?? 'User'}
                </Text>
              </Tooltip>
              {isAdmin ? <VerifyIcon h='3' w='3' /> : null}
            </Flex>

            <IconButton
              variant='outline'
              bgColor={{
                base: 'transparent',
                sm: '#201523',
                md: 'transparent',
                lg: '#201523',
              }}
              borderRadius='lg'
              borderLeftRadius={{ base: 'lg', sm: 0, md: 'lg', lg: 0 }}
              borderWidth={{ base: '1px', sm: 0, md: '1px', lg: 0 }}
              h='11'
              w='11'
              aria-label='User menu'
              _hover={{
                borderColor: '#271C2B',
                bgColor: '#201523',
                bgImage:
                  'radial-gradient(50.92% 50% at 50% 100%, rgba(159, 131, 173, 0.10) 0%, rgba(0, 0, 0, 0.00) 100%)',
              }}
            >
              <ChevronDownIcon
                color='white'
                display={{ base: 'none', sm: 'flex', md: 'none', lg: 'flex' }}
              />
              <UserIcon
                color='#E6E3E8'
                display={{ base: 'flex', sm: 'none', md: 'flex', lg: 'none' }}
              />
            </IconButton>
          </Flex>
        </MenuTrigger>

        <MenuContent>
          <MenuItem id='profile' asChild>
            <Link href='/profile'>
              <UserIcon />
              {t('profile-item')}
            </Link>
          </MenuItem>

          <MenuItem id='settings' asChild>
            <Link href='/profile/settings'>
              <SettingsIcon />
              {t('settings-item')}
            </Link>
          </MenuItem>

          <MenuItem id='withdraw' asChild>
            <Link href='?modal=wallet&tab=withdraw'>
              <WithdrawIcon />
              {t('withdraw-item')}
            </Link>
          </MenuItem>

          <MenuItem id='tip' asChild>
            <Link href='?modal=wallet&tab=tip'>
              <CoinIcon />
              {t('tip-item')}
            </Link>
          </MenuItem>

          <MenuItem id='support' onClick={() => {
            window.Tawk_API?.showWidget();
            window.Tawk_API?.maximize()
          }}>
            <QuestionBallonIcon />
            {t('support-item')}
          </MenuItem>

          <MenuItem id='transactions' asChild>
            <Link href='/profile/transactions'>
              <DollarCellIcon />
              {t('transactions-item')}
            </Link>
          </MenuItem>

          <MenuItem id='affiliate' asChild>
            <Link href='/profile#affiliate'>
              <ShootingStarIcon />
              {t('affiliate-item')}
            </Link>
          </MenuItem>

          <MenuItem id='rewards' asChild>
            <Link href='/profile/rewards'>
              <ChestIcon />
              {t('rewards-item')}
            </Link>
          </MenuItem>

          <MenuSeparator />

          <MenuItem id='logout' asChild>
            <Link href='?modal=logout'>
              <LogoutIcon />
              {t('logout-item')}
            </Link>
          </MenuItem>
        </MenuContent>
      </Menu>

      <AlertDialog
        open={isOpen}
        onOpenChange={() => {
          isOpen ? router.back() : null
        }}
        title={t('alert-dialog.title')}
        description={t('alert-dialog.description')}
        cancelText={t('alert-dialog.cancel-button')}
        confirmText={t('alert-dialog.confirm-button')}
        onConfirm={async () => {
          await logout()
          router.push('/')
        }}
      />
    </>
  )
}
